/**
 * Billing settings — manage the active subscription: summary, manage/cancel, receipts.
 * Plan selection lives on the Plan page; this page manages what's active.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, Settings2 } from "lucide-react";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useMe } from "@/hooks/use-auth";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PLAN_CARDS, type PlanKey } from "@/data/plans";
import type { BillingInvoice } from "@/lib/api-types";

const PAID = ["STARTER", "STUDIO", "PRO", "AGENCY"];

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}
function fmtINR(paise: number) {
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
}
function titleCase(s?: string | null) {
  if (!s) return "";
  return s.charAt(0) + s.slice(1).toLowerCase();
}

export function BillingSettingsPage() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const isAdmin = me?.role === "ADMIN";

  const plan = (me?.plan as string | undefined) ?? "FREE";
  const cycle = me?.planCycle ?? null;
  const status = me?.planStatus ?? "active";
  const periodEnd = me?.planPeriodEnd ?? null;
  const canceledAt = me?.planCanceledAt ?? null;
  const purgeAt = me?.planPurgeAt ?? null;
  const dunningUntil = me?.planDunningUntil ?? null;
  const label = me?.limits?.label ?? plan;
  const isPaid = PAID.includes(plan);
  const isAnnual = cycle === "annual";

  const card = PLAN_CARDS.find((p) => p.key === (plan as PlanKey));
  const priceLine = isPaid && card
    ? isAnnual
      ? `${card.annualPriceLabel}/yr`
      : `${card.priceLabel}/mo`
    : null;

  const [notice, setNotice] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const invoicesQ = useQuery({
    queryKey: ["billing", "invoices"],
    queryFn: () => api.get<{ invoices: BillingInvoice[] }>("/billing/invoices"),
    enabled: isAdmin,
    staleTime: 30_000,
  });

  async function doCancel() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.post<{
        ok: boolean; cycle: string; accessUntil?: string;
        monthsRefunded?: number; refundPaise?: number; refundPending?: boolean;
      }>("/billing/cancel", {});
      if (res.cycle === "annual") {
        const parts: string[] = [`Your plan is cancelled. Access continues until ${fmtDate(res.accessUntil)}.`];
        if (res.refundPaise && res.refundPaise > 0) {
          parts.push(`${fmtINR(res.refundPaise)} for ${res.monthsRefunded} month(s) will be refunded to your original payment method within ~5–7 business days.`);
        } else if (res.refundPending) {
          parts.push(`Your pro-rata refund is being processed and will reach your original payment method shortly.`);
        }
        setNotice(parts.join(" "));
      } else {
        setNotice(`Your plan is cancelled. You keep access until ${fmtDate(res.accessUntil)}, then move to the Free plan.`);
      }
      setTimeout(() => qc.invalidateQueries({ queryKey: ["auth", "workspace-me"] }), 1500);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not cancel. Please try again.");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  return (
    <div>
      <SettingsPageHeader title="Billing" subtitle="Your subscription and payment history." />

      {notice && (
        <div className="mb-6 rounded-xl border border-brand/40 bg-brand-soft/40 px-4 py-3 text-sm text-foreground">
          {notice}
        </div>
      )}

      {!isPaid && purgeAt && (
        <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-foreground">
          Your workspace is on the Free plan. Files and team members beyond the Free limits (2 GB, 2 members)
          will be removed on <span className="font-semibold">{fmtDate(purgeAt)}</span> — oldest files first, keeping
          your most recent 2 GB. Download what you need, or <Link to="../plan" relative="path" className="text-brand hover:underline">resubscribe</Link> to keep everything.
        </div>
      )}

      {/* Subscription summary */}
      <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/30 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-brand">Current plan</div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="font-display text-3xl font-black tracking-tight">{label}</span>
              {priceLine && <span className="text-sm text-muted-foreground">{priceLine}</span>}
            </div>
            {isPaid ? (
              <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                <div>{isAnnual ? "Annual billing" : "Monthly billing"}</div>
                {canceledAt ? (
                  <div className="text-foreground">
                    Cancelled — access until <span className="font-medium">{fmtDate(periodEnd)}</span>, then Free.
                  </div>
                ) : isAnnual ? (
                  <div>Expires <span className="font-medium text-foreground">{fmtDate(periodEnd)}</span> · does not auto-renew.</div>
                ) : (
                  <div>Renews <span className="font-medium text-foreground">{fmtDate(periodEnd)}</span>.</div>
                )}
                {status === "past_due" && (
                  <div className="text-red-500">
                    Payment failed{dunningUntil ? ` — please fix it by ${fmtDate(dunningUntil)} to avoid dropping to Free` : ", please update your payment method"}.
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                You're on the Free plan.
              </p>
            )}
          </div>

          <div className="flex flex-col items-stretch gap-2 sm:flex-row">
            <Link
              to="../plan"
              relative="path"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition-colors hover:opacity-90"
            >
              <Settings2 className="h-4 w-4" /> Manage plan
            </Link>
            {isAdmin && isPaid && !canceledAt && (
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="inline-flex items-center justify-center rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel plan
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Invoices */}
      {isAdmin && (
        <div className="mt-8">
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Payment history</h2>
          {invoicesQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : invoicesQ.data && invoicesQ.data.invoices.length > 0 ? (
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full min-w-[520px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesQ.data.invoices.map((inv) => (
                    <tr key={inv.id} className="border-t border-border">
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.createdAt)}</td>
                      <td className="px-4 py-3">
                        {titleCase(inv.planKey)} {inv.kind === "order" ? "· annual" : "· monthly"}
                      </td>
                      <td className="px-4 py-3">{fmtINR(inv.amountPaise)}</td>
                      <td className="px-4 py-3">
                        <span className={cn("capitalize", inv.status === "refunded" ? "text-muted-foreground" : "text-foreground")}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <a href={`/api/billing/invoices/${inv.id}/receipt`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">
                          <Download className="h-3.5 w-3.5" /> Receipt
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No payments yet.</p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Receipts are generated by Editlock. Questions about a payment? Email support@editlock.io.
          </p>
        </div>
      )}

      {/* Cancel confirmation */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-lg">
            <h3 className="font-display text-lg font-bold tracking-tight">Cancel your {label} plan?</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              {isAnnual
                ? `You'll keep access until the end of your current month (${fmtDate(periodEnd)}), and we'll refund the remaining full months of your annual plan (pro-rata) to your original payment method within ~5–7 business days. Your workspace then moves to Free, with a 15-day window to view and download your files. After that, files and team members beyond the Free plan's limits are removed.`
                : `You'll keep access until ${fmtDate(periodEnd)} — the end of your current billing cycle. There's no refund for the current period. Your workspace then moves to Free, with a 15-day window to view and download your files. After that, files and team members beyond the Free plan's limits are removed.`}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Keep my plan
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={doCancel}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                {busy ? "Cancelling…" : "Cancel plan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
